"use client";

import { motion, useMotionValue, useSpring } from "framer-motion";
import Image from "next/image";
import { useRef, useState } from "react";
import { IconPlayRing } from "@/components/icons/IconPlayRing";
import { IconPlayTriangle } from "@/components/icons/IconPlayTriangle";

const YOUTUBE_ID = "_uz7moHHVPc";

// How far (px) the button can travel from its resting centre
const MAX_TRAVEL = 56;

export function AboutHero() {
  const containerRef = useRef<HTMLDivElement>(null);
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  // Lazy, weighted follow — feels like the button is floating and catches up
  const x = useSpring(rawX, { stiffness: 60, damping: 18, mass: 1.4 });
  const y = useSpring(rawY, { stiffness: 60, damping: 18, mass: 1.4 });

  const [playing, setPlaying] = useState(false);

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    // Map cursor position directly — clamp to ±MAX_TRAVEL so it never flies off-screen
    const halfW = rect.width / 2;
    const halfH = rect.height / 2;
    rawX.set((dx / halfW) * MAX_TRAVEL);
    rawY.set((dy / halfH) * MAX_TRAVEL);
  }

  function handleMouseLeave() {
    rawX.set(0);
    rawY.set(0);
  }

  return (
    <section className="pt-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* biome-ignore lint/a11y/noStaticElementInteractions: mouse tracking for magnetic play button visual effect */}
        {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: mouse tracking for magnetic play button visual effect */}
        <div
          className="relative overflow-hidden rounded-2xl bg-zinc-900"
          onMouseLeave={handleMouseLeave}
          onMouseMove={playing ? undefined : handleMouseMove}
          ref={containerRef}
        >
          {/* Thumbnail + magnetic play button — fades out when video starts */}
          <div
            className="transition-opacity duration-700"
            style={{
              opacity: playing ? 0 : 1,
              pointerEvents: playing ? "none" : "auto",
            }}
          >
            <Image
              alt='"Project Exodus" ad shows three men with arms linked.'
              className="w-full object-cover"
              height={738}
              priority
              sizes="(max-width: 1400px) 100vw, 1312px"
              src={"/images/about/About_HeroImage.webp"}
              width={1312}
            />
          </div>
          {/* Magnetic play button — centered in image */}
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            style={{
              opacity: playing ? 0 : 1,
              pointerEvents: playing ? "none" : undefined,
            }}
          >
            <motion.div
              className="pointer-events-auto relative h-24 w-24 cursor-pointer"
              onClick={() => setPlaying(true)}
              style={{ x, y }}
            >
              {/* Subtle backdrop circle for readability */}
              <div
                aria-hidden="true"
                className="absolute inset-[-16px] rounded-full backdrop-blur-[3px]"
                style={{ background: "rgba(0,0,0,0.22)" }}
              />
              {/* Rotating "WATCH THE OVERVIEW" circular text ring */}
              <motion.div
                animate={{ rotate: -360 }}
                aria-hidden="true"
                className="absolute inset-0"
                style={{ willChange: "transform" }}
                transition={{
                  duration: 12,
                  ease: "linear",
                  repeat: Number.POSITIVE_INFINITY,
                }}
              >
                <IconPlayRing className="h-full w-full" />
              </motion.div>
              {/* Static play icon at centre */}
              <div className="absolute inset-0 flex items-center justify-center">
                <IconPlayTriangle height={50} width={50} />
              </div>
            </motion.div>
          </div>

          {/* YouTube iframe — fades in when play is clicked */}
          {playing && (
            <iframe
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 h-full w-full"
              src={`https://www.youtube.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0`}
              title="Project Exodus Overview"
            />
          )}
        </div>
      </div>
    </section>
  );
}
